import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { popupsTasks } from '@gem/popups';

import {
  IMediaDevicesPresenter,
  useMediaDevicesPresenter,
} from './PreviewActionsPanel.mediaDevices.presenter';

export interface IPreviewActionsPanelPresenter {
  isAudioPopoverOpened: boolean;
  isVideoPopoverOpened: boolean;
  setAudioInput(deviceId: string, needToPublish?: boolean): void;
  setAudioOutput(deviceId: string, needToPublish?: boolean): void;
  setVideoInput(deviceId: string, needToPublish?: boolean): void;
  closeAudioPopover(): void;
  closeVideoPopover(): void;
  onAudioArrowClick(): void;
  onVideoArrowClick(): void;
  getDevices(): Promise<void>;
  showVideoPermissionWarning(): void;
  showAudioPermissionWarning(): void;
}

interface IPreviewActionsPanelPresenterProps {
  audioOutputDeviceId: string | null;
  audioInputDeviceId: string | null;
  videoInputDeviceId: string | null;
  videoDevicesDisabled: boolean;
  audioDevicesDisabled: boolean;
  setCurrentAudioInput(deviceId: string, needToPublish?: boolean): void;
  setCurrentVideoInput(deviceId: string, needToPublish?: boolean): void;
  setCurrentAudioOutput(deviceId: string, needToPublish?: boolean): void;
}

export const usePreviewActionsPanelPresenter = (
  props: IPreviewActionsPanelPresenterProps
): {
  presenter: IPreviewActionsPanelPresenter;
  audioInputPresenter: IMediaDevicesPresenter;
  audioOutputPresenter: IMediaDevicesPresenter;
  videoInputPresenter: IMediaDevicesPresenter;
} => {
  const { t } = useTranslation();
  const audioInputPresenter = useMediaDevicesPresenter({
    onCurrentDeviceChanged: props.setCurrentAudioInput,
    deviceKind: 'audioinput',
    deviceId: props.audioInputDeviceId,
  });

  const videoInputPresenter = useMediaDevicesPresenter({
    onCurrentDeviceChanged: props.setCurrentVideoInput,
    deviceKind: 'videoinput',
    deviceId: props.videoInputDeviceId,
  });

  const audioOutputPresenter = useMediaDevicesPresenter({
    onCurrentDeviceChanged: props.setCurrentAudioOutput,
    deviceKind: 'audiooutput',
    deviceId: props.audioOutputDeviceId,
  });

  const presenter = useLocalObservable<IPreviewActionsPanelPresenter>(() => ({
    isAudioPopoverOpened: false,
    isVideoPopoverOpened: false,

    setAudioInput(deviceId: string): void {
      audioInputPresenter.setCurrentDeviceId(deviceId);
      presenter.closeAudioPopover();
    },

    setAudioOutput(deviceId: string): void {
      audioOutputPresenter.setCurrentDeviceId(deviceId);
      presenter.closeAudioPopover();
    },

    setVideoInput(deviceId: string): void {
      videoInputPresenter.setCurrentDeviceId(deviceId);
      presenter.closeVideoPopover();
    },

    closeAudioPopover(): void {
      presenter.isAudioPopoverOpened = false;
    },

    closeVideoPopover(): void {
      presenter.isVideoPopoverOpened = false;
    },

    onAudioArrowClick(): void {
      presenter.isAudioPopoverOpened = true;
    },

    onVideoArrowClick(): void {
      presenter.isVideoPopoverOpened = true;
    },

    async getDevices(): Promise<void> {
      const devices = await navigator.mediaDevices.enumerateDevices();
      audioInputPresenter.getDevices(devices);
      videoInputPresenter.getDevices(devices);
      audioOutputPresenter.getDevices(devices);
    },

    showVideoPermissionWarning(): void {
      popupsTasks.showAlert(t('calls_no_camera_permissions_web'));
    },

    showAudioPermissionWarning(): void {
      popupsTasks.showAlert(t('calls_no_microphone_permissions_web'));
    },
  }));

  useEffect(() => {
    window.navigator.mediaDevices.addEventListener('devicechange', presenter.getDevices);
    return () => {
      window.navigator.mediaDevices.removeEventListener('devicechange', presenter.getDevices);
    };
  }, []);

  useEffect(() => {
    if (!props.audioDevicesDisabled || !props.videoDevicesDisabled) {
      presenter.getDevices();
    }
  }, [props.audioDevicesDisabled, props.videoDevicesDisabled]);

  return { presenter, audioInputPresenter, audioOutputPresenter, videoInputPresenter };
};
